import { AuthProviderInterface } from './index';
import { useNavigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { Clerk } from '@clerk/clerk-js';
import { getAuth, onAuthStateChanged, signInAnonymously, signOut as firebaseSignOut } from 'firebase/auth';
import { signInWithClerkToken } from "./firebaseAuth";
import logging from "utils/logging";

const log = logging.getLogger("clerk-auth");
log.setLevel('info');

const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

class ClerkAuthProvider extends AuthProviderInterface {
  static instance = null;

  constructor() {
    if (ClerkAuthProvider.instance) {
      return ClerkAuthProvider.instance;
    }
    super();
    this.clerk = new Clerk(publishableKey);
    this.initialized = false;
    this.loadPromise = null;
    this.listener = null;
    ClerkAuthProvider.instance = this;
  }

  static getInstance() {
    if (!ClerkAuthProvider.instance) {
      ClerkAuthProvider.instance = new ClerkAuthProvider();
    }
    return ClerkAuthProvider.instance;
  }

  async init() {
    if (this.initialized) return;
    // Share a single in-flight load() so the pre-render init (index.js) and the
    // in-tree auth provider don't kick off two concurrent Clerk loads.
    if (!this.loadPromise) {
      this.loadPromise = this.clerk
        .load()
        .then(() => {
          log.debug("Clerk Initialized with key: ", publishableKey);
          this.initialized = true;
        })
        .catch((error) => {
          // Let a failed load be retried later instead of latching a rejected
          // promise forever.
          this.loadPromise = null;
          throw error;
        });
    }
    return this.loadPromise;
  }

  async getCurrentUser() {
    if (!this.initialized) {
      await this.init();
    }
    return this.loadUserModelForClerkUser(this.clerk.user);
  }

  async getAnonymousUser() {
    try {
      // Use Firebase's anonymous auth

      const auth = getAuth();
      const currentUser = await new Promise((resolve) => {
        if (auth.currentUser) {
          resolve(auth.currentUser);
          return;
        }

        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
          unsubscribe();
          resolve(firebaseUser);
        });
      });

      log.debug("Current User: ", currentUser);

      if (currentUser) {
        log.debug("Already signed in, returning current user");
        return currentUser;
      }

      log.debug("Signing in anonymously");
      const userCredential = await signInAnonymously(auth);
      const firebaseUser = userCredential.user;
      
      // Convert Firebase user to our user model format
      return {
        uid: firebaseUser.uid,
        displayName: null,
        email: null,
        emailVerified: false,
        isAnonymous: true,
        photoURL: null,
      };
    } catch (error) {
      log.error("Error getting anonymous user:", error);
      return null;
    }
  }

  loadUserModelForClerkUser(user) {
    if (!user) {
      return null;
    }

    return {
      uid: user.id,
      displayName: user.fullName,
      email: user.primaryEmailAddress?.emailAddress,
      emailVerified: user.emailAddresses?.some(email => email.verification?.status === 'verified'),
      isAnonymous: false,
      photoURL: user.imageUrl,
    };
  }

  watchAuthedUser(callback) {
    if (!this.initialized) {
      return this.init().then(() => {
        return this.setupListener(callback);
      });
    } else {
      return this.setupListener(callback);
    }
  }

  setupListener(callback) {
    // Clean up any existing listener
    if (this.listener) {
      // Return a no-op function instead of nothing
      return () => {
        log.debug("No-op unsubscribe called - listener was already set");
      };
    }

    const syncUser = async (user) => {
      const serializedUser = this.loadUserModelForClerkUser(user);
      log.debug("Got clerk User: ", serializedUser);

      log.debug("Clerk Session: ", this.clerk.session);

      // Try the 'firebase' JWT template first; fall back to the default session token
      // if the template isn't configured — both are verifiable with CLERK_JWT_KEY.
      let token = null;
      if (this.clerk.session) {
        try {
          token = await this.clerk.session.getToken({ template: 'firebase' });
        } catch (error) {
          log.warn("Clerk 'firebase' JWT template unavailable, falling back to default token:", error.message);
        }
        if (!token) {
          try {
            token = await this.clerk.session.getToken();
          } catch (error) {
            log.error("Error getting default Clerk token:", error);
          }
        }
      }

      if (user && token) {
        log.debug("Clerk Token acquired, ensuring Firebase auth...");

        const auth = getAuth();
        const currentFirebaseUser = auth.currentUser;

        // Sign in if no Firebase session, or if the Firebase UID doesn't match the Clerk user.
        if (!currentFirebaseUser || currentFirebaseUser.uid !== user.id) {
          log.debug("Signing into Firebase with Clerk token...");
          try {
            const userCredentials = await signInWithClerkToken(token);
            log.debug("Firebase sign-in successful:", userCredentials?.user?.uid);
          } catch (error) {
            log.error("Error signing Firebase in with Clerk token:", error);
          }
        }
      } else if (user && !token) {
        log.warn("Clerk user present but could not obtain a token — Firebase auth will not be established.");
      }

      callback(serializedUser, token);
    };

    this.listener = this.clerk.addListener(({ user }) => {
      syncUser(user);
    });

    syncUser(this.clerk.user);

    // Return a function that will clean up the listener
    return () => {
      log.debug("Unsubscribing from clerk listener");
      if (this.listener) {
        this.listener();
        this.listener = null;
      }
    };
  }

  async logOut() {
    try {
      // Clean up the listener when logging out
      if (this.listener) {
        this.listener();
        this.listener = null;
      }
      await this.clerk.signOut();
      await firebaseSignOut(getAuth());
      return true;
    } catch (error) {
      console.error('Error signing out:', error);
      return false;
    }
  }

  async signIn() {
    return this.clerk.openSignIn();
  }

  async signUp() {
    return this.clerk.openSignUp();
  }

  async getToken() {
    return this.clerk.session?.getToken();
  }

  getProviderComponent() {
    // Wire Clerk's navigation through React Router so all of Clerk's internal
    // navigations (multi-step auth flow, forceRedirectUrl/fallbackRedirectUrl)
    // are soft client-side transitions instead of hard window.location reloads.
    // Without this, Clerk falls back to a full-page redirect that can land the
    // user on the instance default (homepage) instead of forceRedirectUrl.
    // Requires this provider to be rendered inside the Router (see App.js).
    // Pass the singleton clerk-js instance so the <SignIn>/<SignUp> widgets and
    // the Clerk->Firebase token bridge (syncUser) share ONE Clerk session.
    // Without this, ClerkProvider hot-loads its own clerk-js build from the CDN
    // and the two instances can desync: the widget signs the user in while
    // this.clerk.session stays null, so the Firebase exchange never happens.
    // init() already awaited load() before render (src/index.js), and both
    // ClerkProvider and clerk-js guard load() on `loaded`, so this won't reload.
    return ({ children }) => {
      const navigate = useNavigate();
      return (
        <ClerkProvider
          Clerk={this.clerk}
          publishableKey={publishableKey}
          routerPush={(to) => navigate(to)}
          routerReplace={(to) => navigate(to, { replace: true })}
        >
          {children}
        </ClerkProvider>
      );
    };
  }
}

export { ClerkAuthProvider };
